import {
    LinearProgress,
    Typography,
    linearProgressClasses,
    styled,
} from '@mui/material';

import Box from '@mui/material/Box';
import { Languages, normalizeLanguageCode } from 'stores/store';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { MTypography } from 'components/MTypography';
import React from 'react';
import UnplugAnimation from 'components/UnplugAnimation';
import moment from 'moment';
import { useStores } from 'stores/storeInitializer';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 15,
    width: '80%',
    margin: '0 auto',
    borderRadius: 15,
    // borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor:
            theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        // borderRadius: 5,
        backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
    },
}));

export const resolveMomentLocale = (language?: string | null): string => {
    const normalized = normalizeLanguageCode(language);
    if (normalized === Languages.ro) {
        return 'ro';
    }
    if (normalized === Languages.el) {
        return 'el';
    }
    return 'en-gb';
};

export const SessionPreview: React.FC = () => {
    const { store } = useStores();
    const currentSessionProgress = React.useMemo(() => {
        if (!store.session || store.session.targetTotalCost === 0) {
            return 0;
        }
        return +(
            (100 *
                (store.session.currentCumulatedRoundedPriceInclVat ||
                    store.session.currentCumulatedRoundedPrice ||
                    0)) /
            store.session.targetTotalCost
        ).toFixed(2);
    }, [store.session]);

    const estimatedCompletionSeconds = React.useMemo(() => {
        if (
            !store.session ||
            !store.session?.targetTotalCost ||
            store.session?.targetTotalCost === 0 ||
            !store.session?.currentTotalDurationSecs ||
            store.session?.currentTotalDurationSecs === 0 ||
            !store.session?.currentCumulatedRoundedPrice ||
            store.session?.currentCumulatedRoundedPrice === 0
        ) {
            return null;
        }
        moment.locale(resolveMomentLocale(store.language));
        return moment
            .duration(
                (store.session.targetTotalCost -
                    (store.session.currentCumulatedRoundedPriceInclVat ||
                        store.session.currentCumulatedRoundedPrice)) *
                    (store.session.currentTotalDurationSecs /
                        (store.session.currentCumulatedRoundedPriceInclVat ||
                            store.session.currentCumulatedRoundedPrice)),
                'seconds',
            )
            .humanize(true);
    }, [store.session, store.language]);

    return (
        <Box>
            <div style={{ marginTop: 50 }}>
                {currentSessionProgress < 100 ? (
                    <MTypography
                        sx={{
                            padding: '1rem',
                            whiteSpace: 'wrap',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: `5rem`,
                        }}
                        color="text.secondary"
                        variant="h1"
                        values={[Math.round(+currentSessionProgress || 0)]}
                    >
                        {LocalizationKeys.SessionProgress}
                    </MTypography>
                ) : (
                    <UnplugAnimation />
                )}
            </div>
            {currentSessionProgress < 100 ? (
                <BorderLinearProgress
                    variant="determinate"
                    value={parseFloat(currentSessionProgress.toString())}
                />
            ) : null}

            {currentSessionProgress < 100 ? (
                <MTypography
                    hidden={!estimatedCompletionSeconds}
                    sx={{
                        padding: '1rem',
                        whiteSpace: 'wrap',
                        width: '90%',
                        textAlign: 'center',
                        display: 'block',
                    }}
                    variant="button"
                    color="text.secondary"
                    values={[estimatedCompletionSeconds || '']}
                >
                    {LocalizationKeys.EstimatedTimeRemaining}
                </MTypography>
            ) : (
                <Typography
                    sx={{
                        padding: '1rem',
                        whiteSpace: 'wrap',
                        width: '90%',
                        textAlign: 'center',
                        display: 'block',
                    }}
                    variant="button"
                    color="text.secondary"
                >
                    {store.L(LocalizationKeys.TheAmountHasBeenConsumed)}
                    <br />
                    {store.L(LocalizationKeys.ReadyToUnplug)}
                </Typography>
            )}
        </Box>
    );
};
